import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, VStack, Heading, Center, Spinner, Text, Button, ButtonText,
} from '@gluestack-ui/themed';
import GradeHeader from '@/student/grade/components/parts/GradeHeader';
import { ExamVersionStack } from '@/student/grade/components/ExamVersionStack';
import OralExamSection from '@/student/grade/components/parts/OralExamSection';
import EssayDimensionInsights from '@/student/grade/components/parts/EssayDimensionInsights';
import TaskFeedbackList from '@/student/grade/components/parts/TaskFeedbackList';
import MCQResultsCard from '@/student/grade/components/parts/MCQResultsCard';
import type { MCQResultData } from '@/student/grade/components/parts/MCQResultsCard';
import type { Task } from '@/common/api/models/task';
import type { Module } from '@/student/types';
import type { GradedAttemptTab } from '@/common/api/models/gradedAttemptTab';
import { useGradeReportData } from '@/student/grade/hooks/useGradeReportData';
import { EvaluationGradingConfigProvider } from '@/lecturer/EvaluationDesk/context/EvaluationGradingConfigContext';

interface GradeReportProps {
  module: Module;
  tasks: Task[];
  /** Rendered directly under the grade card, before question and task feedback. */
  afterHeader?: React.ReactNode;
  /** When set, replaces task feedback sections (header + version stack still render). */
  reportBodyReplacement?: React.ReactNode;
  archiveState?: 'ready' | 'loading' | 'error';
  onRetryArchive?: () => void;
  gradedAttempts?: GradedAttemptTab[];
  selectedAttemptIndex?: number;
  onSelectAttempt?: (index: number) => void;
}

const getMcqResultData = (task: Task): MCQResultData | null => {
  const mcqResult = task.submission?.mcqResult;
  if (!mcqResult) return null;
  return {
    questions: [],
    studentAnswers: {},
    score: mcqResult.score ?? 0,
    maxScore: mcqResult.maxScore ?? 0,
    passed: mcqResult.passed ?? false,
  };
};

const GradeReport: React.FC<GradeReportProps> = ({
  module,
  tasks,
  afterHeader,
  reportBodyReplacement,
  archiveState = 'ready',
  onRetryArchive,
  gradedAttempts = [],
  selectedAttemptIndex = 0,
  onSelectAttempt,
}) => {
  const { t } = useTranslation();
  const latestIndex = gradedAttempts.length - 1;
  const archiveBody = archiveState === 'loading' ? (
    <Center py="$8" testID="exam-attempt-archive-loading">
      <Spinner />
    </Center>
  ) : archiveState === 'error' ? (
    <VStack space="md" py="$4" testID="exam-attempt-archive-error">
      <Text>{t('student.grade.versions.archiveLoadError')}</Text>
      <Button
        action="primary"
        onPress={() => {
          onRetryArchive?.();
        }}
        testID="exam-attempt-archive-retry"
      >
        <ButtonText>{t('student.grade.versions.retry')}</ButtonText>
      </Button>
    </VStack>
  ) : null;
  const reportBody = reportBodyReplacement ?? archiveBody;
  const data = useGradeReportData(module, tasks);
  const adminOverrideComment = module.grading?.adminOverride?.comment?.trim();
  const hasAdminOverride = !!adminOverrideComment;
  const hasSuppressedTaskType = !hasAdminOverride
    && (data.oralExamTasks.length > 0 || data.delayedFeedbackMcqs.length > 0);
  const hasVersionStack = gradedAttempts.length >= 2 && !!onSelectAttempt;

  if (!module.grading) {
    return null;
  }

  const gradeHeader = (
    <GradeHeader
      moduleTitle={module.title}
      grade={module.grading.grade}
      comment={
        hasAdminOverride
          ? adminOverrideComment
          : (hasSuppressedTaskType ? undefined : module.grading?.comment)
      }
      showFeedback={hasAdminOverride || !hasSuppressedTaskType}
      showAdminOverrideLabel={hasAdminOverride}
      moduleImageUrl={module.imageUrl}
      module={module}
      attachBelow={hasVersionStack}
      lookingBack={hasVersionStack && selectedAttemptIndex !== latestIndex}
    />
  );

  return (
    <EvaluationGradingConfigProvider snapshot={module.gradingConfigSnapshot ?? null}>
      <Box flex={1}>
        <VStack space="md">
          {hasVersionStack ? (
            <ExamVersionStack
              gradedAttempts={gradedAttempts}
              selectedIndex={selectedAttemptIndex}
              onSelect={onSelectAttempt}
            >
              {gradeHeader}
            </ExamVersionStack>
          ) : (
            gradeHeader
          )}

          {afterHeader}

          {hasAdminOverride ? null : (reportBody ?? (
            <>
              {data.oralExamTasks.length > 0 && (
                <OralExamSection tasks={data.oralExamTasks} />
              )}

              {data.delayedFeedbackMcqs.length > 0 && (
                <Box p="$2">
                  <VStack space="lg">
                    <Heading size="md">MCQ Quiz Results</Heading>
                    <VStack space="sm">
                      {data.delayedFeedbackMcqs.map((task) => (
                        <MCQResultsCard
                          key={task.id}
                          task={task}
                          mcqResult={getMcqResultData(task)}
                        />
                      ))}
                    </VStack>
                  </VStack>
                </Box>
              )}

              {data.showEssayInsights && (
                <EssayDimensionInsights tasks={data.essayTasks} />
              )}

              {data.transferTasks.length > 0 && (
                <TaskFeedbackList
                  tasks={data.transferTasks}
                  title={t('grade.transferTasks')}
                  showLevels={false}
                />
              )}

              {data.regularTasks.length > 0 && (
                <TaskFeedbackList
                  tasks={data.regularTasks}
                  title={t('grade.regularTasks')}
                  showLevels
                />
              )}
            </>
          ))}
        </VStack>
      </Box>
    </EvaluationGradingConfigProvider>
  );
};

export default GradeReport;
